import React from 'react';

import { CgSpinner } from 'react-icons/cg';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useHistory, useLocation } from 'react-router-dom';
import { req } from 'react-reqq-lite';
import PropTypes from 'prop-types';
import { HiOutlineBell } from 'react-icons/hi';

import { Button } from 'modules/common/input';
import Badge from 'modules/common/components/Badge';
import {
  renderStatusBadge,
  renderPaymentMethodBadge,
} from 'modules/merchant/order-management/all-orders/constants';
import useOnClickOutside from 'modules/common/hooks/useOnClickOutside';
import { useGetListener } from 'modules/common/firebase/hooks';
import { NavbarButton } from 'modules/common';

import {
  AUTH_TYPE,
  TAB_OPTS,
  NOTIFICATION_TYPE_V2,
  GET_NOTIFICATION,
} from '../constants';
import NotificationList from './NotificationList';
import {
  useGetNotification,
  usePatchNotification,
  useGetNewNotification,
  usePostMarkAllAsRead,
} from '../hooks';
import ReminderToast from './ReminderToast';

const TRIGGER_TYPE = Object.entries(NOTIFICATION_TYPE_V2)?.map(
  (item) => item?.[0]
);

const DEFAULT_FILTER = {
  page: 1,
  per_page: 20,
  new: 1,
  notif_types: TRIGGER_TYPE,
};

function Notification({ showDropdown, setShowDropdown }) {
  const authType = React.useContext(AUTH_TYPE);
  const listener = useGetListener();
  const location = useLocation();

  const audio = React.useMemo(() => new Audio('/audio/notification.wav'), []);
  const [filters, setFilters] = React.useState(DEFAULT_FILTER);
  const [activeTab, setActiveTab] = React.useState(TAB_OPTS?.[0]);
  const [isReloading, setIsReloading] = React.useState(false);
  const [isSeeMore, setIsSeeMore] = React.useState(false);
  const [isShowReminder, setIsShowReminder] = React.useState(false);
  const activeId = React.useRef();
  const ref = React.useRef();

  useOnClickOutside(ref, setShowDropdown);

  const history = useHistory();
  const [isLoading, list, pagination, refresh, append, pushNotification] =
    useGetNotification({
      params: filters,
    });
  const [isPatchLoading, patch] = usePatchNotification();
  const [isPostLoading, post] = usePostMarkAllAsRead();
  const [isNewLoading, , newPagination, newRefresh, includes] =
    useGetNewNotification({
      params: {
        ...DEFAULT_FILTER,
        per_page: 1,
      },
    });

  const handleOnWatch = React.useCallback(() => {
    audio?.pause();
    audio.currentTime = 0;
    audio?.play();
  }, [audio]);
  // eslint-disable-next-line no-unused-vars
  const refreshHandler = React.useCallback(
    (cb) => {
      refresh(
        (params) => ({
          ...params,
          params: {
            ...params?.params,
            per_page: list?.length > 0 ? list?.length + 1 : 20,
          },
        }),
        cb
      );
      newRefresh();
      setIsReloading(true);
    },
    [list?.length, newRefresh, refresh]
  );
  const handleOnShowMore = () => {
    setIsSeeMore(true);
    append({
      params: {
        ...filters,
        per_page: DEFAULT_FILTER?.per_page,
        page: filters?.page + 1,
      },
    });
  };
  const handleOnMarkAllRead = () => post();
  const handleOnActiveTab = (v) => () => {
    setActiveTab(v);
    setFilters((prevState) => ({ ...prevState, page: 1 }));
  };
  const renderDispatch = React.useCallback(
    (row) =>
      ({
        ADMIN: {
          order: '',
        },
        MERCHANT: {
          order: `/orders/all-orders/${row?.model_uuid}`,
          inventory: `/inventory/${row?.model_uuid}`,
        },
        BRANCH: {
          order: `/orders/all-orders/${row?.model_uuid}`,
          rider: `/riders/${row?.model_uuid}`,
          inventory: `/inventory/${row?.model_uuid}`,
        },
      }?.[authType]),
    [authType]
  );
  const handleOnDispatch = React.useCallback(
    (row, type) => () => {
      const { is_new } = row || {};

      const id = row?.id;
      activeId.current = id;

      if (is_new) {
        patch({ id });
      }

      if (location?.pathname?.includes(row?.model_uuid)) {
        history.push(renderDispatch({ model_uuid: '' })?.[type?.code]);
      }

      setTimeout(() => {
        history.push(renderDispatch(row)?.[type?.code]);
      }, 10);

      const width = window.outerWidth;

      if (width < 640) setShowDropdown(false);
    },
    [patch, history, renderDispatch, setShowDropdown, location?.pathname]
  );
  const renderLayout = React.useCallback(
    (row) => {
      const notificationType = NOTIFICATION_TYPE_V2?.[row?.code];
      const $row = row?.[notificationType?.code] || {};

      const Wrapper = (props) => {
        const { children } = props || {};
        return (
          <Button
            label={children}
            className={cn(
              'btn p-2.5 px-3 hover:bg-[#f7f7f7] w-full text-left !rounded-none focus:bg-[#eee]',
              {
                'bg-[#f7f7f7]': !row?.is_new,
                '!bg-[#eee]': row?.id === activeId.current,
              }
            )}
            onClick={handleOnDispatch(row, notificationType)}
          />
        );
      };

      if (['inventory', 'rider']?.includes(notificationType?.code)) return null;

      const args = {
        order: (
          <Wrapper>
            <div className="flex flex-col gap-1.5 text-left w-full text-black">
              <div className="flex justify-between gap-1 items-start leading-tight">
                <div className="flex items-center gap-1.5">
                  {row?.is_new ? (
                    <div className="h-2 w-2 bg-primary-500 rounded-full -mt-px" />
                  ) : null}
                  <div className="font-bold text-[13px] text-gray-600">
                    {notificationType?.label}
                  </div>
                </div>

                <div className="text-xxs text-gray-400 text-right flex-shrink-0">
                  {dayjs(row?.created_at).fromNow()}
                </div>
              </div>

              <div className="flex flex-col text-xs text-gray-500 gap-0.5 leading-none">
                <div className="flex items-center gap-1 mb-px">
                  <div>#{$row?.order_number}</div>
                </div>
                <div>{$row?.customer_name}</div>
                <div className="items-center gap-1 flex-wrap hidden">
                  <Badge
                    type={
                      renderStatusBadge[$row?.system_status]?.type ||
                      renderStatusBadge?.DEFAULT?.type
                    }
                    label={
                      renderStatusBadge[$row?.system_status]?.label ||
                      renderStatusBadge?.DEFAULT?.label
                    }
                    size="sm"
                    className="truncate border"
                  />
                  <Badge
                    type={
                      renderPaymentMethodBadge[$row?.payment_method]?.type ||
                      renderPaymentMethodBadge?.DEFAULT?.type
                    }
                    label={
                      renderPaymentMethodBadge[$row?.payment_method]?.label ||
                      renderPaymentMethodBadge?.DEFAULT?.label
                    }
                    size="sm"
                    className="truncate border"
                  />
                </div>
              </div>
            </div>
          </Wrapper>
        ),
        default: (
          <Wrapper>
            <div className="flex flex-col gap-2 text-left w-full text-black">
              <div className="flex justify-between gap-1 items-start leading-tight">
                <div className="font-bold text-xs text-gray-600">
                  {notificationType?.label}
                </div>
                <div className="text-xxs text-gray-400 text-right flex-shrink-0">
                  {dayjs(row?.created_at).fromNow()}
                </div>
              </div>
            </div>
          </Wrapper>
        ),
      };

      return (
        args?.[notificationType?.code] || args?.[row?.code] || args?.default
      );
    },
    [handleOnDispatch]
  );
  const renderNotificationList = () => (
    <NotificationList
      activeTab={activeTab}
      isReloading={isReloading}
      isSeeMore={isSeeMore}
      isLoading={isLoading}
      pagination={pagination}
      // list={list?.filter((item) => TRIGGER_TYPE?.includes(item?.code))}
      list={list}
      filters={filters}
      includes={includes}
      newPagination={newPagination}
      isPostLoading={isPostLoading}
      onShowMore={handleOnShowMore}
      onMarkAllRead={handleOnMarkAllRead}
      onActiveTab={handleOnActiveTab}
      renderLayout={renderLayout}
      onRefresh={refreshHandler}
    />
  );

  const formatNotification = (data) => {
    const code = NOTIFICATION_TYPE_V2?.[data?.notif_type]?.code;
    let payload = JSON.parse(data?.payload || null);

    payload = {
      id: payload?.[code]?.notifUuid,
      model_uuid: data?.model_uuid,
      code: data?.notif_type,
      is_new: 1,
      created_at: payload?.[code]?.created_at,
      [code]: payload?.[code],
    };

    return payload;
  };

  const reminderData = React.useMemo(() => {
    const { data } = listener || {};
    return formatNotification(data);
  }, [listener]);

  React.useEffect(() => {
    setFilters((prevState) => ({ ...prevState, ...activeTab?.filters }));
  }, [activeTab?.filters]);

  React.useEffect(() => {
    if (isLoading || isNewLoading) return;

    setIsReloading(false);
  }, [isLoading, isNewLoading]);

  React.useEffect(() => {
    if (isLoading) return;

    setIsSeeMore(false);
  }, [isLoading]);

  React.useEffect(() => {
    if (!listener) return;

    const { data } = listener || {};

    if (!TRIGGER_TYPE?.includes(data?.notif_type)) return;

    const payload = formatNotification(data);

    pushNotification(payload, () => {
      handleOnWatch();
    });
  }, [handleOnWatch, listener, pushNotification]);

  React.useEffect(() => {
    if (!showDropdown) return;

    if (filters?.new === 1) {
      req.set(GET_NOTIFICATION, (state) => ({
        ...(state[GET_NOTIFICATION] || {}),
        data: (state[GET_NOTIFICATION]?.data || [])?.filter(
          (item) => item?.is_new === 1
        ),
      }));
    }
  }, [showDropdown, filters?.new]);

  React.useEffect(() => {
    if (listener?.data?.notif_type !== 'ADVANCED_ORDER_REMINDER') return;

    setIsShowReminder(true);
  }, [listener]);

  return (
    <>
      <div className="relative" ref={ref}>
        <div
          className="relative"
          onClick={() => setShowDropdown(!showDropdown)}
          role="presentation"
        >
          <div className="absolute -right-0.5 -top-0.5">
            {isNewLoading || isPatchLoading ? (
              <CgSpinner className="h-[11px] w-[11px] animate-spin m-px mr-0.5" />
            ) : (
              <>
                {includes?.totalNew > 0 && (
                  <div className="rounded-full bg-primary-500 text-white text-[8px] p-px px-[5px]">
                    {includes?.totalNew || '-'}
                  </div>
                )}
              </>
            )}
          </div>

          <NavbarButton icon={(props) => <HiOutlineBell {...props} />} />
        </div>

        {showDropdown && (
          <>
            <div className="rounded-md overflow-hidden absolute w-72 right-0 bg-white shadow border z-[22] -mt-1 hidden md:block">
              {renderNotificationList()}
            </div>
            <div className="block fixed md:hidden transition-all duration-300 delay-100 z-40 w-screen h-screen bg-white left-0 top-0 inset-0">
              <div className="absolute inset-0 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-5 py-3 pb-1">
                  <p className="text-xl text-black font-bold">Notifications</p>
                  <div className="ml-auto">
                    <button
                      type="button"
                      tabIndex="-1"
                      className="rounded-full transition duration-300 ease-in-out group outline-none group hover:bg-primary-500 p-1"
                      onClick={() => setShowDropdown(!showDropdown)}
                    >
                      <svg
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        className="w-6 h-6 transition duration-300 ease-in-out group-hover:text-white text-primary-500 rounded-full"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="4"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {renderNotificationList()}
              </div>
            </div>
          </>
        )}
      </div>

      {reminderData?.code === 'ADVANCED_ORDER_REMINDER' && isShowReminder && (
        <ReminderToast
          data={reminderData}
          onViewDetails={handleOnDispatch(
            reminderData,
            NOTIFICATION_TYPE_V2?.ADVANCED_ORDER_REMINDER
          )}
          onClose={() => setIsShowReminder(!isShowReminder)}
        />
      )}
    </>
  );
}

Notification.propTypes = {
  showDropdown: PropTypes.bool.isRequired,
  setShowDropdown: PropTypes.instanceOf(Function).isRequired,
};

export default Notification;
